import React, { useMemo, useState } from 'react'
import {
  CheckCircleOutlined,
} from '@ant-design/icons'
import { Tag, Modal, Form, Input, Alert, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import { formatDateTime, formatDate } from '@utils/dateTime'

interface MerchantOption {
  id: string;
  name: string;
}

interface InventoryHoldListService extends TableService {
  batchRelease: (ids: string[], note?: string) => Promise<{ success: boolean; message?: string }>;
}

interface InventoryHoldListProps {
  service: InventoryHoldListService;
  role?: 'admin' | 'operator';
  merchants?: MerchantOption[];
  hideMerchantUI?: boolean;
}

interface InventoryHoldRow {
  id: string;
  number?: string;
  reason?: string;
  total_quantity?: number;
  note?: string;
  expires_on?: string | null;
  held_by?: string;
  merchant?: { name?: string } | null;
  created_at?: string;
  [key: string]: unknown;
}

const InventoryHoldList: React.FC<InventoryHoldListProps> = ({ service, role = 'operator', merchants = [], hideMerchantUI = false }) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)
  const [releaseModalVisible, setReleaseModalVisible] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [releasing, setReleasing] = useState(false)
  const [form] = Form.useForm()

  const reasonOptions = [
    'customer_reserve', 'quality_issue', 'regulatory', 'damage', 'recall', 'other'
  ].map(r => ({
    label: t(`enums.inventory_hold.reason.${r}`, { defaultValue: r }),
    value: r
  }))

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: 'models.inventory_hold.number',
      persist: true,
      cell: ({ id, number }: InventoryHoldRow) => (
        <Link to={`/inventory_holds/${id}`}>{number}</Link>
      ),
    },
    {
      nameKey: 'models.inventory_hold.reason',
      cell: ({ reason }: InventoryHoldRow) => (
        <Tag color="warning">
          {t(`enums.inventory_hold.reason.${reason}`, { defaultValue: reason })}
        </Tag>
      )
    },
    {
      nameKey: 'models.inventory_hold.total_quantity',
      selector: ({ total_quantity }: InventoryHoldRow) => total_quantity,
    },
    {
      nameKey: 'models.inventory_hold.note',
      selector: ({ note }: InventoryHoldRow) => note,
      wrap: true,
    },
    {
      nameKey: 'models.inventory_hold.expires_on',
      cell: ({ expires_on }: InventoryHoldRow) => expires_on ? formatDate(expires_on) : '-',
    },
    {
      nameKey: 'models.inventory_hold.held_by',
      selector: ({ held_by }: InventoryHoldRow) => held_by,
    },
    ...(role === 'operator' && !hideMerchantUI ? [{
      nameKey: 'models.merchant.name',
      selector: ({ merchant }: InventoryHoldRow) => merchant?.name,
    }] : []),
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'inventory_holds.created_at',
      cell: ({ created_at }: InventoryHoldRow) => formatDateTime(created_at as string),
    }
  ], [t, role, hideMerchantUI])

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('shared.inventory_holds.status_label'),
      component: MultipleValue,
      inputName: 'status',
      fixed: true,
      defaultValue: ['active'],
      defaultOptions: [
        { label: t('shared.inventory_holds.status.active'), value: 'active' },
        { label: t('shared.inventory_holds.status.released'), value: 'released' },
      ]
    },
    ...(merchants.length > 0 && !hideMerchantUI ? [{
      label: t('models.merchant.name'),
      component: MultipleValue,
      inputName: 'merchant_id',
      defaultOptions: merchants.map(m => ({ label: m.name, value: m.id }))
    }] : []),
    {
      label: t('models.inventory_hold.reason'),
      component: MultipleValue,
      inputName: 'reason',
      defaultOptions: reasonOptions
    },
    // 商品 SKU 篩選比照 InventoriesList：以 products.sku 文字篩選（IN）。
    // 僅 operator 後端 do_filter 實作此 dotted key 過濾，故只在 operator role 顯示此欄。
    ...(role === 'operator' ? [
      {
        label: t('models.inventory.product_sku'),
        component: MultipleValue,
        inputName: 'products.sku',
        createable: true,
      },
    ] : []),
    {
      label: t('common.created_at'),
      component: FullDayRangePicker,
      inputName: 'created_at'
    }
  ], [t, role, merchants, reasonOptions, hideMerchantUI])

  const handleBatchRelease = (selected: Set<string>) => {
    setSelectedIds(selected)
    setReleaseModalVisible(true)
  }

  const confirmBatchRelease = async () => {
    try {
      setReleasing(true)
      const values = await form.validateFields()
      const res = await service.batchRelease(Array.from(selectedIds), values.release_note)
      if (res.success) {
        message.success(t('shared.inventory_holds.release_success'))
        setReleaseModalVisible(false)
        form.resetFields()
        setLastModified(Date.now())
      } else {
        message.error(res.message || (t('common.error.operation_failed') as string))
      }
    } catch (err) {
      if ((err as { errorFields?: unknown }).errorFields) return
      console.error('Failed to batch release inventory holds:', err)
      message.error(t('common.error.operation_failed') as string)
    } finally {
      setReleasing(false)
    }
  }

  const actions = useMemo<TableAction[]>(() => [
    {
      name: 'shared.inventory_holds.batch_release',
      icon: <CheckCircleOutlined />,
      type: 'primary',
      do: handleBatchRelease as (selected: any) => void
    }
  ], [])

  return (
    <>
      <TableIndex
        tableId="InventoryHoldsTable"
        columns={columns}
        supportRules={supportRules}
        actions={actions}
        service={service}
        lastModified={lastModified}
        setLastModified={setLastModified}
        defaultQuery={{
          sorting_field: 'inventory_holds.created_at',
          sorting_direction: 'desc'
        }}
      />

      <Modal
        title={
          <span>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            {t('shared.inventory_holds.batch_release_title')}
          </span>
        }
        open={releaseModalVisible}
        onOk={confirmBatchRelease}
        onCancel={() => { setReleaseModalVisible(false); form.resetFields() }}
        confirmLoading={releasing}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
      >
        <Alert
          message={t('shared.inventory_holds.batch_release_confirm', { count: selectedIds.size })}
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form form={form} layout="vertical">
          <Form.Item name="release_note" label={t('shared.inventory_holds.release_note_label')}>
            <Input.TextArea
              rows={4}
              placeholder={t('shared.inventory_holds.release_note_placeholder') as string}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default InventoryHoldList
